import { useEffect, useMemo, useState } from 'react'

import { RubricArtifactCanvasLayout } from '@/components/admin/evaluation-rubrics/rubric-artifact-canvas-layout'
import { RubricCriteriaEditor } from '@/components/admin/evaluation-rubrics/rubric-criteria-editor'
import { ArtifactCanvasSaveActions } from '@/components/artifact/artifact-canvas-layout'
import { type RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import { validateCriteriaDraft } from '@/lib/rubric-publish-payload'

type RubricCriteriaCanvasProps = {
  criteria: RubricCriterion[]
  kinds: Array<RubricCriterion['kind']>
  skillOptions?: string[]
  focusCriterionKey?: string
  onSave: (criteria: RubricCriterion[]) => void
  onClose: () => void
}

export function RubricCriteriaCanvas({
  criteria,
  kinds,
  skillOptions = [],
  focusCriterionKey,
  onSave,
  onClose,
}: RubricCriteriaCanvasProps) {
  const [draft, setDraft] = useState(() => structuredClone(criteria))

  useEffect(() => {
    setDraft(structuredClone(criteria))
  }, [criteria])

  useEffect(() => {
    if (!focusCriterionKey) return
    const target = document.getElementById(`criterion-${focusCriterionKey}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusCriterionKey])

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(criteria),
    [criteria, draft],
  )

  const validationMessage = useMemo(() => validateCriteriaDraft(draft), [draft])

  function handleSave() {
    if (validationMessage) return
    onSave(draft)
  }

  return (
    <RubricArtifactCanvasLayout
      inlineError={validationMessage}
      actions={
        <ArtifactCanvasSaveActions
          onClose={onClose}
          onSave={handleSave}
          isDirty={isDirty}
          isSaving={false}
          saveDisabled={Boolean(validationMessage)}
        />
      }
    >
      <RubricCriteriaEditor
        criteria={draft}
        onChange={setDraft}
        kinds={kinds}
        variant="canvas"
        depth="l2"
        skillOptions={skillOptions}
        focusCriterionKey={focusCriterionKey}
      />
    </RubricArtifactCanvasLayout>
  )
}
